import { type PartialPropsOf } from '@blaze-runtime/engine-domain';

import {
  applyParentStyleOverrideRules,
  applySimpleAlignParentContentOverrides,
  type ParentStyleOverrideRule,
} from '../../utils/elements/props-builder.utils';
import { PropsBuilder } from '../__base/props-builder';
import {
  type FormChildrenContentOverrides,
  type FormChildrenStyleOverrides,
} from '../form-container/form-container.model';

const SLIDER_FORM_CHILD_STYLE_CLEAR: PartialPropsOf<'slider'> = {
  style: {
    general: { title: undefined, accent: undefined },
    handle: { fill: undefined, outline: undefined },
    track: { active: undefined, inactive: undefined },
  },
};

function getSliderFormChildStyleRules(
  overrides: FormChildrenStyleOverrides,
): ParentStyleOverrideRule<'slider'>[] {
  const { titleLabel, fieldLabel, fieldBox } = overrides;

  return [
    {
      value: titleLabel?.color,
      path: 'style.general.title',
      patch: { style: { general: { title: titleLabel?.color } } },
    },
    {
      value: fieldBox?.accent,
      path: 'style.general.accent',
      patch: { style: { general: { accent: fieldBox?.accent } } },
    },
    {
      value: fieldBox?.accent,
      path: 'style.track.active',
      patch: { style: { track: { active: fieldBox?.accent } } },
    },
    {
      value: fieldLabel?.color,
      path: 'style.value.color',
      patch: { style: { value: { color: fieldLabel?.color } } },
    },
    {
      value: fieldLabel?.color,
      path: 'style.limits.color',
      patch: { style: { limits: { color: fieldLabel?.color } } },
    },
    {
      value: fieldBox?.fill,
      path: 'style.handle.fill',
      patch: { style: { handle: { fill: fieldBox?.fill } } },
    },
    {
      value: fieldBox?.outline,
      path: 'style.handle.outline',
      patch: { style: { handle: { outline: fieldBox?.outline } } },
    },
  ];
}

export class SliderPropsBuilder extends PropsBuilder<'slider'> {
  override setMargin = (margin: string | undefined): this => {
    this.patchProps({ style: { general: { offset: { margin } } } });
    return this;
  };

  override setParentContentProps = (
    elementProps: PartialPropsOf<'slider'>,
    overrides: FormChildrenContentOverrides | null,
    initialValues?: PartialPropsOf<'slider'>,
  ): this => {
    applySimpleAlignParentContentOverrides(
      (patch) => this.patchProps(patch),
      elementProps,
      overrides,
      initialValues,
    );
    return this;
  };

  override setParentStyleProps = (
    elementProps: PartialPropsOf<'slider'>,
    overrides: FormChildrenStyleOverrides | null,
    initialValues?: PartialPropsOf<'slider'>,
  ): this => {
    applyParentStyleOverrideRules(
      (patch) => this.patchProps(patch),
      elementProps,
      overrides,
      initialValues,
      SLIDER_FORM_CHILD_STYLE_CLEAR,
      overrides ? getSliderFormChildStyleRules(overrides) : [],
    );
    return this;
  };

  constructor(props: PartialPropsOf<'slider'> | undefined) {
    super(props, { hasData: true, hasLabel: true });
  }
}
